/**
 * TaskActionBar — Submit button + inline error block.
 *
 * Per CLAUDE.md ERROR 4: when the readiness pre-flight refuses, render the
 * blocking error inline at the route level. We do NOT toast and we do NOT
 * call the user "ready" when they aren't.
 *
 * Submit flow is two-phase (Photo Coverage Part 3):
 *   1. verifyPhotoCoverage  — Haiku vision checks that the uploaded photos
 *      cover all required categories. Writes the verdict to
 *      cleaning_tasks.photo_coverage_status.
 *   2. submit               — fires on 'covered' immediately. On
 *      'incomplete', renders a red blocker and never submits. On
 *      'unavailable' (Anthropic upstream outage), CLAUDE.md ERROR 16
 *      requires red/blocking UI distinct from 'skipped'/advisory — we
 *      render a red BlockingPanel and require an explicit
 *      "Request supervisor approval & submit" click before submit fires.
 *      The schema already allows submit on 'unavailable' and emits a
 *      room.ai_coverage_override audit on the eventual ready transition;
 *      the supervisor sees an "ReadyScan override — needs your eye" chip on
 *      their pending-inspection queue.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import { useSubmitTask }             from '@/hooks/useSubmitTask'
import { useVerifyPhotoCoverage }    from '@/hooks/useVerifyPhotoCoverage'
import { isAppError }                from '@/lib/errors/AppError'
import { CountersInput }             from './CountersInput'

interface Props {
  taskId:           string
  /** Disabled when local checks (checklist incomplete, photos missing) already fail. */
  locallyBlocked?:  string | null
}

/**
 * localStorage key for the in-progress counter draft. Scoped by task
 * so a cleaner with two tasks open in two tabs keeps independent
 * drafts; cleared on successful submit.
 */
function countersStorageKey(taskId: string): string {
  return `rro:task-counters:${taskId}`
}

interface CountersDraft {
  trashBagCount:          number
  appliancesCleanedCount: number
}

function readCountersDraft(taskId: string): CountersDraft | null {
  try {
    const raw = window.localStorage.getItem(countersStorageKey(taskId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CountersDraft>
    const t = Number(parsed.trashBagCount)
    const a = Number(parsed.appliancesCleanedCount)
    if (!Number.isFinite(t) || !Number.isFinite(a)) return null
    if (t < 0 || a < 0 || t > 10 || a > 10) return null
    return { trashBagCount: Math.floor(t), appliancesCleanedCount: Math.floor(a) }
  } catch {
    // Safari private mode + storage quota / disabled storage all
    // raise here. Treat as no draft and fall back to defaults — the
    // cleaner sees an empty counter, same as today's behavior.
    return null
  }
}

function writeCountersDraft(taskId: string, draft: CountersDraft): void {
  try {
    window.localStorage.setItem(countersStorageKey(taskId), JSON.stringify(draft))
  } catch { /* quota / disabled — same fallback as readCountersDraft */ }
}

function clearCountersDraft(taskId: string): void {
  try {
    window.localStorage.removeItem(countersStorageKey(taskId))
  } catch { /* ignore */ }
}

export function TaskActionBar({ taskId, locallyBlocked }: Props): ReactNode {
  const { t }                                       = useTranslation()
  const verify                                      = useVerifyPhotoCoverage()
  const submit                                      = useSubmitTask()
  const [submitted, setSubmitted]                   = useState(false)
  // Three UI states for the verify-then-submit flow:
  //   coverageBlocker     — 'incomplete' verdict; red blocker; submit never fires.
  //   coverageUnavailable — 'unavailable' verdict (Anthropic outage); red blocker
  //                         with an explicit supervisor-approval CTA; submit
  //                         only fires after the cleaner acknowledges via that
  //                         button (CLAUDE.md ERROR 16).
  //   coverageNotice      — post-success advisory shown after a successful
  //                         submit-with-override so the cleaner knows AI was
  //                         down when they submitted.
  //   coverageTruncated   — the task carried more photos than ReadyScan
  //                         examines in one pass, so the verdict covers a
  //                         subset. Advisory, never blocking: the verdict is
  //                         still valid for what it saw, but presenting a
  //                         partial scan as a full one is the thing to avoid.
  const [coverageBlocker,     setCoverageBlocker]     = useState<string | null>(null)
  const [coverageUnavailable, setCoverageUnavailable] = useState<string | null>(null)
  const [coverageNotice,      setCoverageNotice]      = useState<string | null>(null)
  const [coverageTruncated,   setCoverageTruncated]   = useState<string | null>(null)
  // defectBlocker — 'defects_found' cleanliness verdict (migration
  // 20260622000002); red blocker listing the issues to re-clean. Unlike
  // 'unavailable', there is no cleaner-side "submit anyway": a real defect
  // must be re-cleaned. The supervisor override lives in the approve flow.
  const [defectBlocker,       setDefectBlocker]       = useState<string | null>(null)
  // Operational counters (#74a) — bounded 0–10 by the CountersInput
  // clamp + the RPC CHECK constraint. Both default 0; cleaner adjusts
  // before tapping submit. Persisted to localStorage on every change
  // so the running count survives a mid-task refresh / mobile tab
  // eviction / camera-app round-trip — the failure mode that hurts
  // pay-per-bag billing the most.
  const [trashBagCount, setTrashBagCount]                   = useState<number>(() => readCountersDraft(taskId)?.trashBagCount          ?? 0)
  const [appliancesCleanedCount, setAppliancesCleanedCount] = useState<number>(() => readCountersDraft(taskId)?.appliancesCleanedCount ?? 0)

  useEffect(() => {
    writeCountersDraft(taskId, { trashBagCount, appliancesCleanedCount })
  }, [taskId, trashBagCount, appliancesCleanedCount])

  const inFlight = verify.isPending || submit.isPending

  const onSubmit = async (): Promise<void> => {
    setCoverageBlocker(null)
    setCoverageUnavailable(null)
    setCoverageNotice(null)
    setCoverageTruncated(null)
    setDefectBlocker(null)

    // Phase 1 — photo coverage + cleanliness verification (Haiku vision).
    let verdict
    try {
      verdict = await verify.mutateAsync({ taskId })
    } catch {
      // Hook surfaces the AppError on verify.error; we render it below.
      return
    }

    // Set before the blocking branches below so the disclosure survives an
    // early return — a truncated scan is worth knowing about precisely when
    // the verdict also blocks, since the missing category may be in the part
    // ReadyScan never saw.
    if (verdict.photos_truncated > 0) {
      setCoverageTruncated(t('action.coverageTruncatedDetail', {
        count: verdict.photos_truncated,
      }))
    }

    if (verdict.status === 'incomplete' && verdict.missing_categories.length > 0) {
      setCoverageBlocker(t('action.coverageMissingDetail', {
        categories: verdict.missing_categories.join(', '),
      }))
      return
    }
    if (verdict.quality_status === 'defects_found' && verdict.defects.length > 0) {
      // Cleanliness defect — hard block. Submit's CHECK 4 also refuses
      // with PHOTO_DEFECTS_FOUND, but we surface the issue list here so
      // the cleaner knows exactly what to re-clean before retrying. Each
      // line pairs the category label with the AI's own note describing
      // what it actually saw — a bare label ("Ring in the bathtub") tells
      // the cleaner the category; the note tells them what to fix.
      const labels = verdict.defects
        .map((d) => {
          const label = t(`action.defectLabels.${d.code}`, d.code)
          return d.note ? `${label} — ${d.note}` : label
        })
        .join('; ')
      setDefectBlocker(t('action.defectsFoundDetail', { defects: labels }))
      return
    }
    if (verdict.status === 'unavailable' || verdict.quality_status === 'unavailable') {
      // Red-block the submit and surface the explicit supervisor-approval
      // CTA. CLAUDE.md ERROR 16 — never auto-pass an upstream-outage
      // verdict; the human in the loop must acknowledge the override.
      setCoverageUnavailable(t('action.coverageUnavailableBlockerDetail'))
      return
    }

    // 'covered' — proceed directly. The submit Edge Function's CHECK 3
    // reads photo_coverage_status (just written above).
    await runSubmit({ asOverride: false })
  }

  const onRequestSupervisorApproval = async (): Promise<void> => {
    // Cleaner has acknowledged the AI-unavailable verdict and is
    // explicitly opting into the supervisor-manual-verification path.
    // Clear the blocker and remember the override so the post-success
    // view shows the advisory.
    const detail = coverageUnavailable
    if (!detail) return
    setCoverageUnavailable(null)
    await runSubmit({ asOverride: true })
  }

  const runSubmit = async ({ asOverride }: { asOverride: boolean }): Promise<void> => {
    try {
      await submit.mutateAsync({ taskId, trashBagCount, appliancesCleanedCount })
      clearCountersDraft(taskId)
      if (asOverride) {
        setCoverageNotice(t('action.coverageUnavailablePostSubmitDetail'))
      }
      setSubmitted(true)
    } catch {
      if (asOverride) {
        // Bring the blocker back so the cleaner can retry — never strand
        // them in a state with no path forward.
        setCoverageUnavailable(t('action.coverageUnavailableBlockerDetail'))
      }
      /* submit error rendered via submit.error */
    }
  }

  const onCancelUnavailable = (): void => {
    // Cleaner wants to wait for AI to recover or retake photos. Drop the
    // blocker; the Submit button is rendered again.
    setCoverageUnavailable(null)
  }

  if (submitted && submit.data) {
    // Persist the AI-unavailable advisory into the success view so
    // the cleaner doesn't think AI verified everything when it
    // actually didn't. The supervisor's approve flow is the
    // documented manual override.
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div
          role="status"
          style={{
            background:   'var(--brand-accent-subtle)',
            color:        'var(--brand-accent)',
            border:       '1px solid var(--brand-accent)',
            borderRadius: 'var(--brand-radius-md)',
            padding:      '0.75rem 1rem',
          }}
        >
          <strong>{t('action.submitted')}</strong> {t('action.roomMovedTo', { status: submit.data.room_next_status.replace(/_/g, ' ') })}
        </div>
        {coverageNotice && (
          <NoticePanel title={t('action.coverageUnavailable')} detail={coverageNotice} />
        )}
        {/* Also rendered here, not only on the pre-submit panel: a successful
            submit replaces that whole panel, so a truncation disclosed only
            there would vanish at the exact moment the task goes through —
            which is when it most needs to be on the record for the cleaner. */}
        {coverageTruncated && (
          <NoticePanel
            title={t('action.coverageTruncated')}
            detail={coverageTruncated}
            testId="coverage-truncated-notice"
          />
        )}
      </section>
    )
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {locallyBlocked && (
        <BlockingPanel title={t('action.cannotSubmitYet')} detail={locallyBlocked} />
      )}

      {/* Advisory, not a blocker — NoticePanel, so a partial scan is disclosed
          without implying the task cannot be submitted. */}
      {coverageTruncated && (
        <NoticePanel
          title={t('action.coverageTruncated')}
          detail={coverageTruncated}
          testId="coverage-truncated-notice"
        />
      )}

      {coverageBlocker && (
        <BlockingPanel title={t('action.coverageMissing')} detail={coverageBlocker} />
      )}

      {defectBlocker && (
        <BlockingPanel title={t('action.defectsFound')} detail={defectBlocker} testId="defect-blocker" />
      )}

      {coverageUnavailable && (
        <BlockingPanel
          title={t('action.coverageUnavailable')}
          detail={coverageUnavailable}
          testId="coverage-unavailable-blocker"
        />
      )}

      {verify.error && isAppError(verify.error) && (
        <BlockingPanel title={t('action.submissionBlocked')} detail={verify.error.userMessage} />
      )}

      {submit.error && isAppError(submit.error) && (
        <BlockingPanel title={t('action.submissionBlocked')} detail={submit.error.userMessage} />
      )}

      <CountersInput
        trashBagCount={trashBagCount}
        appliancesCleanedCount={appliancesCleanedCount}
        onTrashBagCountChange={setTrashBagCount}
        onAppliancesCleanedCountChange={setAppliancesCleanedCount}
        disabled={inFlight}
      />

      {coverageUnavailable
        ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <button
              type="button"
              data-testid="request-supervisor-approval-submit"
              onClick={onRequestSupervisorApproval}
              disabled={inFlight}
              style={{
                padding:      '0.7rem 1rem',
                background:   inFlight ? 'var(--brand-neutral-300)' : 'var(--brand-danger)',
                color:        'var(--brand-white)',
                border:       'none',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.95rem',
                fontWeight:   600,
                cursor:       inFlight ? 'not-allowed' : 'pointer',
              }}
            >
              {submit.isPending
                ? t('action.submitting')
                : t('action.coverageUnavailableSubmitAnyway')}
            </button>
            <button
              type="button"
              data-testid="coverage-unavailable-cancel"
              onClick={onCancelUnavailable}
              disabled={inFlight}
              style={{
                padding:      '0.45rem 0.8rem',
                background:   'var(--brand-white)',
                color:        'var(--brand-neutral-700)',
                border:       '1px solid var(--brand-neutral-300)',
                borderRadius: 'var(--brand-radius-md)',
                fontSize:     '0.85rem',
                cursor:       inFlight ? 'not-allowed' : 'pointer',
              }}
            >
              {t('action.coverageUnavailableCancel')}
            </button>
          </div>
        )
        : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={inFlight || !!locallyBlocked}
            style={{
              padding:      '0.7rem 1rem',
              background:   inFlight || locallyBlocked ? 'var(--brand-neutral-300)' : 'var(--brand-primary)',
              color:        'var(--brand-white)',
              border:       'none',
              borderRadius: 'var(--brand-radius-md)',
              fontSize:     '0.95rem',
              fontWeight:   600,
              cursor:       inFlight || locallyBlocked ? 'not-allowed' : 'pointer',
            }}
          >
            {verify.isPending
              ? t('action.checkingPhotos')
              : submit.isPending
                ? t('action.submitting')
                : t('action.submitForInspection')}
          </button>
        )
      }
    </section>
  )
}

function BlockingPanel({ title, detail, testId }: { title: string; detail: string; testId?: string }): ReactNode {
  return (
    <div
      role="alert"
      data-testid={testId}
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.6rem 0.8rem',
        fontSize:     '0.9rem',
      }}
    >
      <strong>{title}</strong>
      <div>{detail}</div>
    </div>
  )
}

function NoticePanel({ title, detail, testId }: { title: string; detail: string; testId?: string }): ReactNode {
  // Non-blocking advisory styling distinct from the danger panel above.
  // Used for the 'unavailable' verdict so the cleaner knows AI was down
  // but submit is still allowed (supervisor manual override path), and for
  // a truncated scan, where the verdict is valid but partial.
  return (
    <div
      role="status"
      data-testid={testId}
      style={{
        background:   'var(--brand-warning-subtle, #fef9c3)',
        color:        'var(--brand-warning, #854d0e)',
        border:       '1px solid var(--brand-warning, #ca8a04)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.6rem 0.8rem',
        fontSize:     '0.9rem',
      }}
    >
      <strong>{title}</strong>
      <div>{detail}</div>
    </div>
  )
}
